<template>
    <form
        ref="form"
        @submit.prevent="submit"
        :action="formRoute"
        method="POST"
        class="relative h-full"
    >
        <page-header
            :submit-button-text="data ? 'Update' : 'Create Bonus'"
            :show-submit-button="true"
            :submit-button-disabled="submitButtonDisabled || !editable"
            submit-button-type="submit"

            :can-delete="canDelete"
            :delete-route="deleteRoute"
            :delete-value="deleteValue"
            :delete-disabled="affiliateLinkChanged"
            :delete-tooltip="affiliateLinkChanged ? 'Please reload page' : ''"
            :delete-options="{
                deleteModalText: deleteModalText,
                buttons: [
                    {
                        buttonText: 'Delete Bonus and Affiliate Link',
                        requestBody: [{ key: 'deleteAffiliateLink', value: 1 }],
                        buttonDisabled: !canDeleteAffiliateLink,
                        buttonTooltip: canDeleteAffiliateLink ? '' : 'Affiliate Link in use',
                    },
                    {
                        buttonText: 'Delete Bonus only',
                        requestBody: [{ key: 'deleteAffiliateLink', value: 0 }],
                        buttonDisabled: false,
                        buttonTooltip: '',
                    },
                ],
            }"

            :can-create="canCreate"
            :create-route="createRoute"
            :create-value="createValue"

            :index-route="indexRoute"
            :index-value="indexValue"
            :title="title"
            :subtitle="subtitle"

            :show-preview-button="false"

            :errors="errors"
            :is-slide-over="isSlideOver"

            :version-id="versionId"
            :init-version-id="initVersionId"
            :last-updated-by="lastUpdatedBy"
        />

        <input-rich-text
            name="editor_note"
            label="Editor's note"
            v-model="form.editor_note"
            :language="form.language"
            :error="errors.editor_note ? errors.editor_note[0] : null"
            :maxlength="60000"
        />
        <p class="my-1 text-xs text-gray-600 dark:text-gray-300 mb-3">The note is for internal use only and won’t be displayed on the site</p>

        <input-text
            name="name"
            label="* Bonus Name (as displayed on frontend)"
            v-model="form.name"
            :error="errors.name ? errors.name[0] : null"
        />
        <input-text
            class="mt-3"
            name="description"
            label="* Bonus Description (as displayed on frontend)"
            v-model="form.description"
            :error="errors.description ? errors.description[0] : null"
        />

        <div
            class="border bg-white dark:bg-gray-700 rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 dark:text-gray-200 mb-4">Bonus Type</p>
            <multi-reference
                create-text="Create new bonus type and link bonus type to this page"
                link-text="Link existing bonus types"
                v-model="form.types"
                model-data="Bonus Type"
                :should-open-new-window="true"
                :language="language"
                :creatable="false"
                :error="errors.types ? errors.types[0] : null"
                :order="{
                    'name': 'asc'
                }"
            />
        </div>

        <input-date
            label="Expiry date"
            name="expiry_date"
            v-model="form.expiry_date"
            class="mt-3"
            :error="errors.expiry_date ? errors.launch_date[0] : null"
        />

        <div
            class="border bg-white dark:bg-gray-700 rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 dark:text-gray-200 mb-4">* Casino</p>
            <reference
                create-text="Create new casino and link casino to this page"
                link-text="Link an existing casino"
                v-model="form.casino"
                model-data="Casino"
                :should-open-new-window="true"
                :error="errors.casino_id ? errors.casino_id[0] : null"
                :deletable="false"
                :creatable="false"
                :linkable="false"
            />
        </div>

        <div
            class="border bg-white dark:bg-gray-700 rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 dark:text-gray-200 mb-4">* Affiliate Link</p>
            <reference
                create-text="Create new affiliate link and link affiliate link to this page"
                link-text="Link an existing affiliate link"
                v-model="form.affiliate_link"
                model-data="Affiliate Link"
                :language="language"
                :should-open-new-window="true"
                :editable="true"
                :linkable="false"
                :error="errors.affiliate_link_id ? errors.affiliate_link_id[0] : null"
                :pre-filled="{
                    casino: preFilledCasino || null,
                }"
            />
        </div>

       <input-checkbox
            name="is_exclusive"
            label="Exclusive Bonus"
            class="mt-6 mb-6"
            v-model="form.is_exclusive"
            :error="errors.is_exclusive ? errors.is_exclusive[0] : null"
        />

        <input-text
            label="Bonus Code"
            name="bonus_code"
            v-model="form.bonus_code"
            :error="errors.bonus_code ? errors.bonus_code[0] : null"
            class="mt-3"
        />

        <input-text
            label="Bonus Percentage"
            name="bonus_percentage"
            v-model="form.bonus_percentage"
            :error="errors.bonus_percentage ? errors.bonus_percentage[0] : null"
            class="mt-3"
        />

        <input-text
            label="Free Spins"
            name="free_spins"
            v-model="form.free_spins"
            :error="errors.free_spins ? errors.free_spins[0] : null"
            class="mt-3"
        />

        <input-checkbox
            name="hide_up_to"
            label="Don't show 'up to' on amount"
            class="mt-3"
            v-model="form.hide_up_to"
            :error="errors.hide_up_to ? errors.hide_up_to[0] : null"
        />

        <input-checkbox
            name="use_free_text"
            label="Use free text"
            class="mt-3"
            v-model="form.use_free_text"
            :error="errors.use_free_text ? errors.use_free_text[0] : null"
        />

        <input-textarea
            label="Free Text"
            name="free_text"
            :disabled="!form.use_free_text"
            v-model="form.free_text"
            :error="errors.free_text ? errors.free_text[0] : null"
            class="mt-3"
            :language="language"
        />

        <currency-amount-list
            v-model="form.amounts"
            name="amounts"
            label="Amounts"
            :language="language"
            :errors="errors"
        />

        <input-rich-text
            label="Terms Short"
            name="terms_short"
            v-model="form.terms_short"
            :error="errors.terms_short ? errors.terms_short[0] : null"
            class="mt-3"
            :language="language"
        />

        <input-rich-text
            label="Terms Full"
            name="terms_full"
            v-model="form.terms_full"
            class="mt-3"
            :error="errors.terms_full ? errors.terms_full[0] : null"
            :language="language"
        />

        <media-reference
            v-model="form.icon"
            class="mt-3"
            label="* Icon"
            :media-types="['Icon']"
            name="icon_id"
        />

        <div
            class="border bg-white dark:bg-gray-700 rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 dark:text-gray-200 mb-4">Event</p>
            <reference
                create-text="Create new event and link event to this page"
                link-text="Link an existing event"
                v-model="form.event"
                model-data="Event"
                :should-open-new-window="true"
                :creatable="false"
                :error="errors.event_id ? errors.event_id[0] : null"
                :order="{
                    'name': 'asc'
                }"
            />
        </div>

        <div
            class="border bg-white dark:bg-gray-700 rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 dark:text-gray-200 mb-4">Available in following countries</p>
            <multi-reference
                create-text="Create new country and link country to this page"
                link-text="Link existing countries to bonus"
                :creatable="false"
                :language="language"
                v-model="form.countries"
                model-data="Country"
                :should-open-new-window="true"
                :full-width="true"
                :excluded-ids="excludedCountries"
                :order="{
                    'name': 'asc'
                }"
                :error="errors.countries ? errors.countries[0] : null"
            />
        </div>

        <input-checkbox
            name="rest_of_the_world"
            label="Available for the rest of the world?"
            class="mt-3"
            v-model="form.rest_of_the_world"
            :error="errors.rest_of_the_world ? errors.rest_of_the_world[0] : null"
        />

        <input type="hidden" name="_token" :value="csrf" />
        <input v-if="data" type="hidden" name="_method" value="patch" />
    </form>
</template>

<script>
import crudForm from "../mixins/crudForm";
import siteLanguage from '../config/siteLanguage'

export default {
    name: "BonusForm",
    mixins: [crudForm],
    props: {
        language: {
            default: () => {
                return {
                    id: siteLanguage.language.ENGLISH,
                    iso: siteLanguage.iso.ENGLISH,
                    slug: siteLanguage.iso.ENGLISH,
                };
            },
            required: false,
        },
    },
    data() {
        return {
            affiliateLinkChanged: false,
            form: {
                editor_note: this.getFormData("editor_note"),
                name: this.getFormData("name"),
                expiry_date: this.getFormData("expiry_date"),
                types: this.getFormData("types"),
                event: this.getFormData("event"),
                event_id: this.getFormData("event_id"),
                casino: this.getFormData("casino"),
                casino_id: this.getFormData("casino_id"),
                affiliate_link: this.getFormData("affiliate_link"),
                affiliate_link_id: this.getFormData("affiliate_link_id"),
                bonus_code: this.getFormData("bonus_code"),
                bonus_percentage: this.getFormData("bonus_percentage"),
                free_spins: this.getFormData("free_spins"),
                hide_up_to: this.getFormData("hide_up_to", false),
                use_free_text: this.getFormData("use_free_text", false),
                free_text: this.getFormData("free_text"),
                amounts: this.getFormData("amounts", []),
                terms_short: this.getFormData("terms_short"),
                terms_full: this.getFormData("terms_full"),
                description: this.getFormData("description"),
                countries: this.getFormData("countries"),
                is_exclusive: this.getFormData("is_exclusive", false),
                rest_of_the_world: this.getFormData("rest_of_the_world", false),
                icon_id: this.getFormData("icon_id"),
                icon: this.getFormData("icon"),
            },
            allCountries: [],
        };
    },
    watch: {
        "form.casino": function (newValue) {
            this.form.casino_id = newValue ? newValue.id : null;
        },
        "form.event": function (newValue) {
            this.form.event_id = newValue ? newValue.id : null;
        },
        "form.type": function (newValue) {
            this.form.type_id = newValue ? newValue.id : null;
        },
        "form.affiliate_link": function (newValue) {
            this.affiliateLinkChanged = true;
            this.form.affiliate_link_id = newValue ? newValue.id : null;
        },
        "form.icon": function(newValue){
            this.form.icon_id = newValue?.id || null;
        },
        "form.use_free_text": function (newValue) {
            if (!newValue) {
                this.form.free_text = null;
            }
        },
    },
    computed: {
        preFilledCasino(){
            return this.preFilled?.casino;
        },
        excludedCountries(){
            if(!this.form.casino) return [];
            return this.form.casino.restricted_countries.map(country => country.id);
        },
        canDeleteAffiliateLink(){
            if (!this.form.affiliate_link) {
                // form.affiliate_link will be null when creating
                return true;
            }

            let usage = this.form.affiliate_link.usage;
            let usageInBonuses = usage.bonuses.filter(e => e.id !== this.data.id);
            let usageInTextContent = Object.values(usage.textContent).filter(e => e.id !== this.data.id);

            return (
                usageInBonuses.length
                + usage.casinoReviewsTwoColFourTable.length
                + usage.links.length
                + usageInTextContent.length
            ) === 0;
        },
        deleteModalText(){
            if (this.canDeleteAffiliateLink) return '';

            let usage = this.form.affiliate_link.usage;
            let hasSoftDeleted = false;

            let bonuses = usage.bonuses.filter(e => e.id !== this.data.id).map(e => {
                let deletedIndicator = '';
                if (e.trashed) {
                    deletedIndicator = ' <span class="text-red-300">(Deleted)</span>';
                    hasSoftDeleted = true;
                }
                return `<a target="_blank" href="${e.route}">${e.route}${deletedIndicator}</a>`;
            }).join('<br>');

            let casinoReviewsTwoColFourTable = usage.casinoReviewsTwoColFourTable.map(e => {
                let deletedIndicator = '';
                if (e.trashed) {
                    deletedIndicator = ' <span class="text-red-300">(Deleted)</span>';
                    hasSoftDeleted = true;
                }
                return `<a target="_blank" href="${e.route}">${e.route}${deletedIndicator}</a>`;
            }).join('<br>');

            let links = usage.links.map(e => {
                let deletedIndicator = '';
                if (e.trashed) {
                    deletedIndicator = ' <span class="text-red-300">(Deleted)</span>';
                    hasSoftDeleted = true;
                }
                return `<a target="_blank" href="${e.route}">${e.route}${deletedIndicator}</a>`;
            }).join('<br>');

            let textContent = Object.values(usage.textContent).filter(e => e.id !== this.data.id).map(e => {
                let deletedIndicator = '';
                if (e.trashed) {
                    deletedIndicator = ' <span class="text-red-300">(Deleted)</span>';
                    hasSoftDeleted = true;
                }
                return `<a target="_blank" href="${e.route}">${e.route}${deletedIndicator}</a>`;
            }).join('<br>');

            let usages = [
                (bonuses.length ? `<strong>Bonuses:</strong><br>${bonuses}` : ''),
                (casinoReviewsTwoColFourTable.length ? `<strong>Casino Reviews 2/4 Table:</strong><br>${casinoReviewsTwoColFourTable}` : ''),
                (links.length ? `<strong>Links:</strong><br>${links}` : ''),
                (textContent.length ? `<strong>In text content:</strong><br>${textContent}` : ''),
            ].filter(e => e !== '');

            let affiliateLinkRoute = route("affiliateLinks.edit", [this.form.affiliate_link.id]);

            let deletedMessage = hasSoftDeleted ? '<span class="text-red-300">To edit deleted documents, please reach out to a developer.</span><br><br>' : '';

            return `<strong>Affiliate Link "${this.form.affiliate_link.name}"
                        (<a target="_blank" href="${affiliateLinkRoute}">${affiliateLinkRoute}</a>) is in use.
                    </strong><br>
                    To delete the Affiliate Link please remove it from these places:<br><br>
                    ${deletedMessage}
                    ${usages.join('<br><br>')}`;
        },
    }
};
</script>
